import type {ValuationData} from "@reva/shared/valuation-data";

import type ServiceApi from "../../api";
import type {AssignableUser} from "../../redux/persistenceSlice";
import {RevaApi} from "../api-constants";
import {SimpleApiInterface} from "../api-interface";
import type {ApiCredentials} from "../credentials";
import type {ApiExecutor} from "../executor";

/**
 * API interface to load the users a valuation can be assigned to
 * (GET {@link RevaApi.PATH_VALUATIONS}/{id}/assignableUsers).
 */
export class SearchAssignableUsersApiInterface extends SimpleApiInterface<AssignableUser[]> {
    constructor(private readonly revaId: string) {
        super();
    }

    protected buildUrl(api: ServiceApi): string {
        return api.buildRevaApiUrl([RevaApi.PATH_VALUATIONS, this.revaId, RevaApi.PATH_VALUATION_ASSIGNABLE_USERS]);
    }

    protected async executeInternal(
        executor: ApiExecutor,
        url: string,
        credentials: ApiCredentials,
    ): Promise<AssignableUser[]> {
        const response = await executor.fetchGET(url, credentials);
        return response.users;
    }
}

/**
 * API interface to load the finalize-capable users a valuation may be handed to as its new owner when it is
 * reopened for a correction (GET {@link RevaApi.PATH_VALUATIONS}/{id}/finalizeAssignableUsers). Only users who
 * may finalize are returned, with the snapshot authors of this valuation first (see reva-rest
 * listFinalizeAssignableUsers). Same shape as {@link SearchAssignableUsersApiInterface}.
 */
export class SearchFinalizeAssignableUsersApiInterface extends SimpleApiInterface<AssignableUser[]> {
    constructor(private readonly revaId: string) {
        super();
    }

    protected buildUrl(api: ServiceApi): string {
        return api.buildRevaApiUrl([
            RevaApi.PATH_VALUATIONS,
            this.revaId,
            RevaApi.PATH_VALUATION_FINALIZE_ASSIGNABLE_USERS,
        ]);
    }

    protected async executeInternal(
        executor: ApiExecutor,
        url: string,
        credentials: ApiCredentials,
    ): Promise<AssignableUser[]> {
        const response = await executor.fetchGET(url, credentials);
        return response.users;
    }
}

/**
 * API interface to assign a valuation to a user (POST {@link RevaApi.PATH_VALUATIONS}/{id}/assign).
 *
 * When a snapshot is loaded, `snapshotNumber` appends `?snapshot=<n>` so the backend frames the response
 * as that snapshot instead of switching the client to the live state (null when live → param is omitted).
 */
export class AssignUserApiInterface extends SimpleApiInterface<ValuationData> {
    constructor(
        private readonly revaId: string,
        private readonly username: string | null,
        private readonly snapshotNumber: number | null = null,
    ) {
        super();
    }

    protected buildUrl(api: ServiceApi): string {
        return api.buildRevaApiUrl([RevaApi.PATH_VALUATIONS, this.revaId, RevaApi.PATH_VALUATION_ASSIGN], {
            snapshot: this.snapshotNumber,
        });
    }

    protected buildPayload(): object {
        return {username: this.username};
    }

    protected async executeInternal(
        executor: ApiExecutor,
        url: string,
        credentials: ApiCredentials,
    ): Promise<ValuationData> {
        return executor.fetchPOST(url, credentials, this.buildPayload());
    }
}
